import React, { useState, useEffect, useRef, Fragment } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useQuery } from '@apollo/client';
import dayjs from 'dayjs';
import _ from 'lodash';
import Picker from 'emoji-picker-react';
import InputAdornment from '@material-ui/core/InputAdornment';
import randomString from 'random-string';
import PropTypes from 'prop-types';

import CircularProgress from '@material-ui/core/CircularProgress';

import {
    Text,
    Avatar,
    CrossIcon,
    IconButton,
    InputWithEndAdornment,
    HappyIcon,
    InfoCirCleIcon,
    GoBackButton,
    FilledIconButton,
    GalleryIcon,
    NotDeliveredIcon,
    CollapsedIcon,
} from '../index';

import {
    StyledChatContainer,
    StyledChatWindow,
    StyledMesageHeader,
    StyledTextGroup,
    StyledTextContainer,
    StyledChatInputContainer,
    StyledCloudContainer,
    StyledTextCloudContainer,
    StyledTextCloud,
    StyledTextErrorContainer,
    StyledNameContainer,
    StyledPaper,
} from './style';

import { StyledDialog } from '../Dialog/style';

import socket from '../../socket';

import { alertError, clearMessagesReceiver } from '../../redux/stateActions';

import { encrypt, decrypt, groupByDate } from '../../utils/messages';
import readFile from '../../utils/readFile';
import getFileFromUrl from '../../utils/getFileFromUrl';

import {
    GET_MESSAGES,
    GET_PATIENT,
    GET_PROVIDER,
    GET_GROUP_MESSAGES,
    GET_MEET_INFO,
    imageMessage,
} from '../../services/index';

const noImage = '/resources/images/noimage.jpeg';

const ChatWindow = ({ fullScreen, hideGoBack, open, setOpen }) => {
    const dispatch = useDispatch();
    const user = useSelector((state) => state.user);
    const messages = useSelector((state) => state.messages);
    const pickerRef = useRef(null);

    const [loading, setLoading] = useState(false);
    const [openWindow, setOpenWindow] = useState(false);
    const [openPicker, setOpenPicker] = useState(false);
    const [messagesData, setMessagesData] = useState([]);
    const [inputValue, setInputValue] = useState('');
    const [sender, setSender] = useState(null);
    const [receiver, setReceiver] = useState(null);
    const [receiverRole, setReceiverRole] = useState(null);
    const [receiverProfile, setReceiverProfile] = useState({
        name: null,
        avatar: null,
        deactivated: false,
    });
    const [isGroup, setIsGroup] = useState(false);
    const [role, setRole] = useState(null);

    useEffect(() => {
        setRole(localStorage.getItem('role'));
    }, []);

    useEffect(() => {
        if (open !== undefined) {
            setOpenWindow(open);
        }
    }, [open]);

    /*--------------- Get Provider ---------------*/
    const { data: getProviderData, error: getProviderError } = useQuery(GET_PROVIDER, {
        variables: {
            id: receiver,
        },
        skip: !receiver || receiverRole !== 'Provider' || isGroup,
    });

    useEffect(() => {
        if (getProviderError) {
            dispatch(alertError(`Cannot get Provider ${getProviderError.message}`));
        }

        if (getProviderData && !_.isEmpty(getProviderData.staff)) {
            setReceiverProfile({
                name: getProviderData?.staff?.user?.fullname,
                avatar: getProviderData?.staff?.user?.avatar?.url,
                deactivated: getProviderData?.staff.deactivated,
            });
        }
    }, [getProviderError, getProviderData]);

    /*--------------- Get Patient ---------------*/
    const { data: getPatientData, error: getPatientError } = useQuery(GET_PATIENT, {
        variables: {
            id: receiver,
        },
        skip: !receiver || receiverRole !== 'Patient' || isGroup,
    });

    useEffect(() => {
        if (getPatientError) {
            dispatch(alertError(`Cannot get Client ${getPatientError.message}`));
        }

        if (getPatientData && !_.isEmpty(getPatientData.patient)) {
            setReceiverProfile({
                name: !getPatientData?.patient?.user
                    ? 'Inactive'
                    : getPatientData?.patient?.user?.fullname,
                avatar: !getPatientData?.patient?.user
                    ? null
                    : getPatientData?.patient?.user?.avatar?.url,
                deactivated: !getPatientData?.patient?.user ? true : false,
            });
        }
    }, [getPatientError, getPatientData]);

    /*--------------- Get 1 on 1 Message ---------------*/
    const {
        data: getMessagesData,
        error: getMessagesError,
        loading: getMessagesLoading,
        refetch: getMessagesRefetch,
    } = useQuery(GET_MESSAGES, {
        variables: {
            myId: sender,
            targetId: receiver,
        },
        skip: _.isEmpty(user) || !sender || !receiver || isGroup,
    });

    useEffect(() => {
        if (getMessagesError) {
            dispatch(alertError(`Cannot get Messages ${getMessagesError.message}`));
        }

        if (getMessagesData) {
            let concatMessages = _.concat(getMessagesData.meSend, getMessagesData.meReceived);

            const sortMessages = _.orderBy(
                concatMessages,
                [(obj) => new Date(obj.createdAt)],
                ['desc']
            );
            const decryptMessages = sortMessages.map((message) =>
                message.text
                    ? {
                          ...message,
                          text: decrypt(
                              message.text,
                              message.sender === sender ? sender + receiver : receiver + sender
                          ),
                      }
                    : message
            );
            setMessagesData(decryptMessages);
        }
    }, [getMessagesError, getMessagesData]);

    /*--------------- Get Meet Info ---------------*/
    const { data: getMeetInfoData, error: getMeetInfoError } = useQuery(GET_MEET_INFO, {
        variables: {
            id: receiver,
        },
        skip: _.isEmpty(user) || !sender || !receiver || !isGroup,
    });

    useEffect(() => {
        if (getMeetInfoError) {
            dispatch(alertError(`Cannot get Meet info ${getMeetInfoError.message}`));
        }

        if (getMeetInfoData && !_.isEmpty(getMeetInfoData.meet)) {
            const { name } = getMeetInfoData.meet;
            setReceiverProfile({ name: name });
        }
    }, [getMeetInfoError, getMeetInfoData]);

    /*--------------- Get Group Message ---------------*/
    const {
        data: getGroupMessagesData,
        error: getGroupMessagesError,
        refetch: getGroupMessagesRefetch,
    } = useQuery(GET_GROUP_MESSAGES, {
        variables: {
            meetId: receiver,
        },
        skip: _.isEmpty(user) || !sender || !receiver || !isGroup,
    });

    useEffect(() => {
        if (getGroupMessagesError) {
            dispatch(alertError(`Cannot get Group Messages ${getGroupMessagesError.message}`));
        }

        if (getGroupMessagesData) {
            const sortMessages = _.orderBy(
                getGroupMessagesData.messages,
                [(obj) => new Date(obj.createdAt)],
                ['desc']
            );

            const decryptMessages = sortMessages.map((message) => ({
                ...message,
                text: decrypt(message.text, message.sender + message.receiver),
            }));

            setMessagesData(decryptMessages);
        }
    }, [getGroupMessagesError, getGroupMessagesData]);

    useEffect(() => {
        if (receiverProfile.name && !isGroup) {
            getMessagesRefetch();
            if (role !== 'Patient') {
                setOpenWindow(true);
            } else {
                setOpenWindow(open);
            }
        }

        if (receiverProfile.name && isGroup) {
            getGroupMessagesRefetch();
            if (role !== 'Patient') {
                setOpenWindow(true);
            } else {
                setOpenWindow(open);
            }
        }
    }, [receiverProfile, isGroup]);

    const onChange = (e) => {
        setInputValue(e.target.value);
    };

    const handleAddLineBreak = () => {
        setInputValue(`${inputValue}\n`);
    };

    const onEmojiClick = (event, emojiObject) => {
        setInputValue(inputValue + emojiObject.emoji);
    };

    const handleOpenPicker = () => {
        setOpenPicker(!openPicker);
    };

    const handleSendMessage = async () => {
        if (inputValue === '') return;

        setOpenPicker(false);

        // Encrypt
        const encryptData = encrypt(inputValue, sender + receiver);
        const newMessagesData = JSON.parse(JSON.stringify(messagesData));
        const tempId = randomString({ length: 24 }).toLowerCase();

        const newMessage = {
            sender: sender,
            receiver: receiver,
            text: inputValue,
            tempId: tempId,
            createAt: dayjs,
        };

        newMessagesData.unshift({ ...newMessage, error: false, encryptText: encryptData });
        setMessagesData(newMessagesData);

        const data = { sender: sender, receiver: receiver, text: encryptData, tempId: tempId };

        if (isGroup) {
            socket.emit('sendGroupMessage', {
                ...data,
                senderRole: role,
                senderProfile: {
                    user: { fullname: user.fullname, avatar: user.avatar },
                },
            });
        } else {
            socket.emit('sendMessage', {
                ...data,
                senderRole: role,
            });
        }

        setInputValue('');
    };

    useEffect(() => {
        const role = localStorage.getItem('role');

        if (!_.isEmpty(user)) {
            if (['Provider', 'Provider Admin', 'Admin'].includes(role)) {
                setSender(user.staff.id);
                setReceiverRole('Patient');
            }
            if (role === 'Patient') {
                setSender(user.patient.id);
                setReceiverRole('Provider');
            }
        }
    }, [user]);

    useEffect(() => {
        if (messages.id === receiver) return;

        setMessagesData([]);
        setReceiver(messages.id);
        setReceiverProfile({
            avatar: messages.avatar,
            name: messages.name,
            deactivated: messages.deactivated,
        });
        setIsGroup(messages.isGroup);
    }, [messages]);

    useEffect(() => {
        socket.off('receivingMessage');
        socket.on('receivingMessage', (data) => {
            if (data.isGroup) {
                setIsGroup(true);
                if (receiver === data.receiver) {
                    const newMessagesData = JSON.parse(JSON.stringify(messagesData));

                    if (data.text) {
                        const decryptText = decrypt(data.text, data.sender + data.receiver);
                        newMessagesData.unshift({ ...data, text: decryptText });
                    } else {
                        newMessagesData.unshift(data);
                    }
                    setMessagesData(newMessagesData);
                }
                // else {
                //     setReceiver(data.receiver);
                // }
            } else {
                setIsGroup(false);
                if (receiver === data.sender) {
                    const newMessagesData = JSON.parse(JSON.stringify(messagesData));

                    // if message is text
                    if (data.text) {
                        const decryptText = decrypt(data.text, data.sender + data.receiver);
                        newMessagesData.unshift({ ...data, text: decryptText });
                    }
                    // else message is image
                    else {
                        newMessagesData.unshift(data);
                    }
                    setMessagesData(newMessagesData);
                }
                // else {
                //     setReceiver(data.sender);
                // }
            }
        });

        socket.off('sendMessageError');
        socket.on('sendMessageError', (tempId) => {
            const newMessagesData = JSON.parse(JSON.stringify(messagesData));
            const findIndex = newMessagesData.findIndex((m) => m.tempId === tempId);

            if (findIndex !== -1) {
                newMessagesData[findIndex].error = true;
                setMessagesData(newMessagesData);
            }
        });

        socket.off('sendMessageSuccess');
        socket.on('sendMessageSuccess', ({ tempId, id }) => {
            const newMessagesData = JSON.parse(JSON.stringify(messagesData));
            const findIndex = newMessagesData.findIndex((m) => m.tempId === tempId);
            if (findIndex !== -1) {
                newMessagesData[findIndex].id = id;
                delete newMessagesData[findIndex].tempId;
                setMessagesData(newMessagesData);
            }
        });
    }, [receiver, messagesData, openWindow]);

    const handleRetrySendMessage = (tempId) => {
        const newMessagesData = JSON.parse(JSON.stringify(messagesData));
        const findMessage = newMessagesData.find((m) => m.tempId === tempId);

        const obj = { ...findMessage, text: findMessage.encryptText };
        delete obj.encryptText;
        delete obj.error;

        if (isGroup) {
            socket.emit('sendGroupMessage', obj);
        } else {
            socket.emit('sendMessage', obj);
        }
    };

    // hide picker if click somewhere else
    useEffect(() => {
        document.addEventListener('click', handleClickOutside, false);
        return () => {
            document.removeEventListener('click', handleClickOutside, false);
        };
    }, []);

    const handleClickOutside = (event) => {
        if (pickerRef.current && !pickerRef.current.contains(event.target)) {
            setOpenPicker(false);
        }
    };

    /*---------- upload image -----------*/
    const [newFile, setNewFile] = useState(null);
    const [newFileInfo, setNewFileInfo] = useState(null);

    useEffect(() => {
        if (newFile === null) return;

        uploadImage();
    }, [newFile, sender, receiver]);

    const uploadImage = async () => {
        setLoading(true);
        try {
            const nameArr = newFileInfo.name.split('.');

            const name = `message_${sender}_${receiver}_${randomString({
                length: 24,
            }).toLowerCase()}.${nameArr[nameArr.length - 1]}`;

            const myFile = await getFileFromUrl(newFile, name, newFileInfo.type);

            const obj = {
                sender: sender,
                receiver: receiver,
                senderRole: role,
                createAt: dayjs,
            };

            let formData = new FormData();
            formData.append('file', myFile);
            formData.append('data', JSON.stringify(obj));

            const res = await imageMessage(formData);

            setNewFile(null);
            setNewFileInfo(null);

            if (res.status === 200) {
                getMessagesRefetch();
            } else {
                throw new Error(res);
            }
        } catch (error) {
            dispatch(alertError(`cannot send image: ${error.message}`));
        }
        setLoading(false);
    };

    /*---------- preview image -----------*/
    const [preview, setPreview] = useState(null);

    const handlePreview = (url) => {
        setPreview(url);
    };

    const handleClosePreview = () => {
        setPreview(null);
    };

    /*---------- Close Window -----------*/
    const handleCloseChatWindow = () => {
        dispatch(clearMessagesReceiver());
        setReceiver(null);
        setReceiverProfile({
            name: null,
            avatar: null,
            deactivated: false,
        });
        setMessagesData([]);
        setOpenPicker(false);
        setOpenWindow(false);
        if (setOpen !== undefined) {
            setOpen(false);
        }
        setCollapsed(false);
    };

    /*---------- Collapse Window -----------*/
    const [collapsed, setCollapsed] = useState(false);

    const handleCollapseChatWindow = () => {
        setCollapsed(!collapsed);
    };

    return (
        openWindow && (
            <StyledChatContainer fullScreen={fullScreen}>
                <StyledDialog
                    open={preview !== null}
                    onClose={handleClosePreview}
                    aria-labelledby="image-preview-dialog"
                    aria-describedby="image-preview-dialog"
                    PaperComponent={StyledPaper}
                >
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'row',
                            columnGap: '20px',
                            maxWidth: '100vw',
                        }}
                        onClick={handleClosePreview}
                    >
                        <img alt="preview" src={preview} style={{ width: '100%' }} />
                        {/* <IconButton
                            style={{
                                width: '46px',
                                height: '46px',
                                color: 'red',
                            }}
                        >
                            <CloseCircleIcon />
                        </IconButton> */}
                    </div>
                </StyledDialog>
                <StyledChatWindow fullScreen={fullScreen} collapsed={collapsed}>
                    <StyledMesageHeader collapsed={collapsed}>
                        {fullScreen && !hideGoBack && (
                            <GoBackButton onClick={handleCloseChatWindow} disabled={loading} />
                        )}

                        <Avatar
                            src={receiverProfile.deactivated ? null : receiverProfile.avatar}
                            alt={receiverProfile.deactivated ? '' : receiverProfile.name}
                            $avatarSize="30px"
                        />
                        <Text style={{ width: '100%' }}>
                            {receiverProfile.deactivated ? 'Inactive' : receiverProfile.name}
                        </Text>
                        {!fullScreen && (
                            <Fragment>
                                <IconButton onClick={handleCollapseChatWindow}>
                                    <CollapsedIcon />
                                </IconButton>
                                <IconButton
                                    style={{ marginLeft: 'auto' }}
                                    onClick={handleCloseChatWindow}
                                    disabled={loading}
                                >
                                    <CrossIcon width="24px" height="24px" fill="black" />
                                </IconButton>
                            </Fragment>
                        )}
                    </StyledMesageHeader>
                    {!collapsed && (
                        <Fragment>
                            <TextGroup
                                messagesData={messagesData}
                                handleRetrySendMessage={handleRetrySendMessage}
                                sender={sender}
                                handlePreview={handlePreview}
                            />

                            <ChatInput
                                loading={loading}
                                disabled={receiverProfile.deactivated || getMessagesLoading}
                                pickerRef={pickerRef}
                                openPicker={openPicker}
                                onEmojiClick={onEmojiClick}
                                handleOpenPicker={handleOpenPicker}
                                inputValue={inputValue}
                                onChange={onChange}
                                handleAddLineBreak={handleAddLineBreak}
                                handleSendMessage={handleSendMessage}
                                setNewFile={setNewFile}
                                setNewFileInfo={setNewFileInfo}
                            />
                        </Fragment>
                    )}
                </StyledChatWindow>
            </StyledChatContainer>
        )
    );
};

ChatWindow.propTypes = {
    fullScreen: PropTypes.bool,
    hideGoBack: PropTypes.bool,
    open: PropTypes.bool,
    setOpen: PropTypes.func,
    showAvatar: PropTypes.bool,
};

export default ChatWindow;

export const TextGroup = ({
    messagesData,
    handleRetrySendMessage,
    sender,
    showAvatar,
    handlePreview,
}) => {
    return (
        <StyledTextGroup>
            {!_.isEmpty(messagesData) &&
                Object.entries(groupByDate(messagesData)).map(([key, value]) => (
                    <div key={key}>
                        <Text $fontSize="10px" $color="gray3" style={{ textAlign: 'center' }}>
                            {dayjs(key).format('D MMM YYYY')}
                        </Text>
                        <StyledTextContainer>
                            {value.map((message, index) => (
                                <StyledText
                                    key={`messages${index}`}
                                    isMy={message.sender === sender}
                                    data={message}
                                    onRetry={handleRetrySendMessage}
                                    showAvatar={showAvatar}
                                    onPreview={handlePreview}
                                />
                            ))}
                        </StyledTextContainer>
                    </div>
                ))}
        </StyledTextGroup>
    );
};

TextGroup.propTypes = {
    messagesData: PropTypes.arrayOf(PropTypes.object).isRequired,
    handleRetrySendMessage: PropTypes.func.isRequired,
    sender: PropTypes.string.isRequired,
    showAvatar: PropTypes.bool,
    handlePreview: PropTypes.func.isRequired,
};

export const ChatInput = ({
    disabled,
    loading,
    pickerRef,
    openPicker,
    onEmojiClick,
    handleOpenPicker,
    inputValue,
    onChange,
    handleAddLineBreak,
    handleSendMessage,
    setNewFile,
    setNewFileInfo,
}) => {
    const dispatch = useDispatch();

    const inputRef = useRef(null);

    const handleClickUpload = () => {
        inputRef.current.click();
    };

    const onFileChange = async (event) => {
        const newFile = event.target.files[0];
        event.target.value = null;

        if (!['image/png', 'image/gif', 'image/jpeg'].includes(newFile.type)) {
            return dispatch(alertError('invalid file type.'));
        }

        if (newFile.size > 2097152) {
            return dispatch(alertError('file size limit exceeded 2MB.'));
        }

        setNewFileInfo(newFile);
        setNewFile(await readFile(newFile));
    };

    return (
        <StyledChatInputContainer>
            <input
                ref={inputRef}
                type="file"
                onChange={onFileChange}
                accept=".jpeg,.jpg,.png"
                style={{ display: 'none' }}
            />

            <InputWithEndAdornment
                id="message-input"
                type="text"
                autoComplete="off"
                multiline
                placeholder="Type message..."
                disabled={disabled || loading}
                endAdornment={
                    <InputAdornment position="end" style={{ position: 'relative' }} ref={pickerRef}>
                        {openPicker && (
                            <Picker
                                onEmojiClick={onEmojiClick}
                                preload
                                native
                                pickerStyle={{
                                    position: 'absolute',
                                    bottom: '20px',
                                    right: 0,
                                    zIndex: 5,
                                }}
                            />
                        )}
                        <IconButton
                            onClick={() => {
                                handleOpenPicker();
                            }}
                            disabled={disabled || loading}
                        >
                            <HappyIcon fill="black" />
                        </IconButton>
                    </InputAdornment>
                }
                inputPropsStyle={{ paddingLeft: '15px' }}
                style={{ minHeight: '49px', border: 'none' }}
                $backgroundColor="gray8"
                value={inputValue}
                onChange={onChange}
                onKeyDown={(e) => {
                    if ((e.metaKey || e.altKey) && (e.key === 'Enter' || e.which === 13)) {
                        e.preventDefault();
                        handleAddLineBreak();
                    } else if (e.key === 'Enter' || e.which === 13) {
                        e.preventDefault();
                        handleSendMessage();
                    }
                }}
                $inputHeight="auto"
                inputProps={{ maxLength: 500 }}
            />
            {setNewFile && setNewFileInfo && (
                <FilledIconButton
                    $backgroundColor="green4"
                    onClick={handleClickUpload}
                    style={{ margin: 0, padding: 0, width: '43px', height: '43px' }}
                    disabled={disabled || loading}
                >
                    {loading ? (
                        <CircularProgress style={{ width: '25px', height: '25px' }} />
                    ) : (
                        <GalleryIcon />
                    )}
                </FilledIconButton>
            )}
        </StyledChatInputContainer>
    );
};

ChatInput.propTypes = {
    disabled: PropTypes.bool,
    pickerRef: PropTypes.any.isRequired,
    openPicker: PropTypes.bool.isRequired,
    onEmojiClick: PropTypes.func.isRequired,
    handleOpenPicker: PropTypes.func.isRequired,
    inputValue: PropTypes.string.isRequired,
    onChange: PropTypes.func.isRequired,
    handleAddLineBreak: PropTypes.func.isRequired,
    handleSendMessage: PropTypes.func.isRequired,
    setNewFile: PropTypes.func,
    setNewFileInfo: PropTypes.func,
};

export const StyledText = ({ isMy, data, onRetry, showAvatar, onPreview }) => {
    return (
        <StyledCloudContainer isMy={isMy}>
            {!isMy && data.isGroup && (
                <StyledNameContainer>
                    <Text $color="gray3" $fontSize="10px">
                        {data.patient ? data.patient?.user?.fullname : data.staff?.user?.fullname}
                    </Text>
                </StyledNameContainer>
            )}
            <StyledTextCloudContainer isMy={isMy}>
                {data.error && <InfoCirCleIcon />}

                {!isMy && showAvatar && (
                    <Avatar
                        style={{ alignSelf: 'center' }}
                        alt={
                            data.patient ? data.patient?.user?.fullname : data.staff?.user?.fullname
                        }
                        src={
                            data.patient
                                ? data.patient?.user?.avatar?.url
                                : data.staff?.user?.avatar?.url
                        }
                        $avatarSize="30px"
                    />
                )}

                {data.text && (
                    <StyledTextCloud isMy={isMy}>
                        <Text
                            $color={isMy ? 'white' : 'gray1'}
                            $fontSize="14px"
                            style={{ whiteSpace: 'pre-wrap' }}
                        >
                            {data.text}
                        </Text>
                    </StyledTextCloud>
                )}

                {data.image && (
                    <img
                        src={data.image.url}
                        style={{ maxWidth: '150px', cursor: 'pointer' }}
                        onClick={() => onPreview(data.image.url)}
                    />
                )}

                {!data.image && !data.text && <img src={noImage} style={{ maxWidth: '100px' }} />}

                {isMy && showAvatar && (
                    <Avatar
                        style={{ alignSelf: 'center' }}
                        alt={
                            data.patient ? data.patient?.user?.fullname : data.staff?.user?.fullname
                        }
                        src={
                            data.patient
                                ? data.patient?.user?.avatar?.url
                                : data.staff?.user?.avatar?.url
                        }
                        $avatarSize="30px"
                    />
                )}
            </StyledTextCloudContainer>

            <StyledTextErrorContainer isMy={isMy}>
                {!isMy && (
                    <Text $color="gray3" $fontSize="10px">
                        {dayjs(data.createdAt).format('HH:mm')}
                    </Text>
                )}

                {data.error && (
                    <Fragment>
                        <div style={{ display: 'flex', alignItems: 'center', columnGap: '5px' }}>
                            <NotDeliveredIcon />
                            <Text $color="primaryDarkPink" $fontSize="12px">
                                Not delivered
                            </Text>
                        </div>

                        <Text $color="gray1" $fontSize="12px" onClick={() => onRetry(data.tempId)}>
                            Retry
                        </Text>
                    </Fragment>
                )}

                {isMy && (
                    <Text $color="gray3" $fontSize="10px">
                        {dayjs(data.createdAt).format('HH:mm')}
                    </Text>
                )}
            </StyledTextErrorContainer>
        </StyledCloudContainer>
    );
};

StyledText.propTypes = {
    isMy: PropTypes.bool,
    data: PropTypes.object,
    onRetry: PropTypes.func,
    showAvatar: PropTypes.bool,
    onPreview: PropTypes.func,
};
